import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSearch, type HashtagResult, type SearchResult } from '@/hooks/useSearch'
import { SCHEME_URL_REGEX, SCHEMELESS_URL_REGEX } from '@/lib/url'
import type { Protocol } from '@/contexts/ProtocolContext'

interface RichComposerProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    protocol?: Protocol
    borderless?: boolean
    fillHeight?: boolean
}

type SuggestionType = 'mention' | 'hashtag'

// Measure the pixel position of the caret inside a textarea using a mirror div
const mirrorStyles = [
    'direction',
    'boxSizing',
    'width',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'whiteSpace',
    'wordWrap',
    'wordBreak',
] as const

function getCaretCoordinates(
    el: HTMLTextAreaElement,
    position: number
): { top: number; left: number } {
    const div = document.createElement('div')
    const style = getComputedStyle(el)

    div.style.position = 'absolute'
    div.style.visibility = 'hidden'
    div.style.whiteSpace = 'pre-wrap'
    div.style.wordWrap = 'break-word'

    for (const prop of mirrorStyles) {
        div.style.setProperty(
            prop.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`),
            style.getPropertyValue(prop.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`))
        )
    }

    div.textContent = el.value.substring(0, position)

    const span = document.createElement('span')
    span.textContent = el.value.substring(position) || '.'
    div.appendChild(span)

    document.body.appendChild(div)
    const coords = { top: span.offsetTop - el.scrollTop, left: span.offsetLeft - el.scrollLeft }
    document.body.removeChild(div)
    return coords
}

// Escape HTML to prevent XSS - all user input goes through this
// Note: apostrophes are intentionally NOT escaped - they are safe in HTML text
// content (not attributes), and escaping them to &#039; would cause the hashtag
// regex to match the #039 portion, breaking the entity rendering.
const escapeHtml = (text: string): string => {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

export function RichComposer({
    value,
    onChange,
    placeholder,
    className = '',
    protocol,
    borderless = false,
    fillHeight = false,
}: RichComposerProps) {
    const { searchUsers, searchHashtags } = useSearch()
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const highlighterRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const autoGrow = borderless && !fillHeight
    const [suggestions, setSuggestions] = useState<SearchResult[]>([])
    const [hashtagSuggestions, setHashtagSuggestions] = useState<HashtagResult[]>([])
    const [suggestionType, setSuggestionType] = useState<SuggestionType>('mention')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })

    const updateDropdownPos = useCallback(() => {
        if (inputRef.current) {
            const el = inputRef.current
            const rect = el.getBoundingClientRect()
            const caret = getCaretCoordinates(el, el.selectionStart)
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 24
            const caretTop = rect.top + caret.top + lineHeight
            // Clamp so dropdown (max-h-60 = 240px) stays within viewport
            const maxTop = window.innerHeight - 252
            setDropdownPos({
                top: Math.min(caretTop, maxTop),
                left: rect.left,
                width: rect.width,
            })
        }
    }, [])

    // Reposition dropdown on resize/scroll while suggestions are visible
    useEffect(() => {
        if (!showSuggestions) return
        window.addEventListener('resize', updateDropdownPos)
        window.addEventListener('scroll', updateDropdownPos, true)
        return () => {
            window.removeEventListener('resize', updateDropdownPos)
            window.removeEventListener('scroll', updateDropdownPos, true)
        }
    }, [showSuggestions, updateDropdownPos])

    // Clean up old react-mentions format: @[display](id) -> @display
    const cleanOldMentionFormat = (text: string): string => {
        return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
    }

    // On mount, clean up any old react-mentions format in the value
    useEffect(() => {
        const cleaned = cleanOldMentionFormat(value)
        if (cleaned !== value) {
            onChange(cleaned)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Only run on mount

    // Auto-grow textarea and sync container height when borderless and not fillHeight
    useLayoutEffect(() => {
        if (!autoGrow || !inputRef.current) return
        const ta = inputRef.current
        ta.style.height = 'auto'
        ta.style.height = ta.scrollHeight + 'px'
        if (containerRef.current) {
            containerRef.current.style.height = ta.scrollHeight + 'px'
        }
    }, [value, autoGrow])

    // Sync scroll between input and highlighter
    const handleScroll = () => {
        if (autoGrow) return
        if (inputRef.current && highlighterRef.current) {
            highlighterRef.current.scrollTop = inputRef.current.scrollTop
            highlighterRef.current.scrollLeft = inputRef.current.scrollLeft
        }
    }

    // Handle text change
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value
        onChange(newValue)

        const cursorPos = e.target.selectionStart
        const textBeforeCursor = newValue.slice(0, cursorPos)

        // Check for # hashtag trigger first (only for Mastodon)
        // Hashtags must come before mentions because both use similar patterns
        const hashtagMatch = protocol === 'mastodon' ? textBeforeCursor.match(/#(\w*)$/) : null
        // Check for @ mention trigger
        const mentionMatch = textBeforeCursor.match(/@([\w-]*)$/)

        if (hashtagMatch) {
            // Hashtag autocomplete for Mastodon
            const query = hashtagMatch[1]
            setSuggestionType('hashtag')
            void searchHashtags(query).then(results => {
                setHashtagSuggestions(results)
                setSuggestions([])
                setShowSuggestions(results.length > 0)
                setSelectedIndex(0)
                if (results.length > 0) updateDropdownPos()
            })
        } else if (mentionMatch) {
            // Mention autocomplete - trim trailing dashes so partial input like "peter-" queries as "peter"
            const query = mentionMatch[1].replace(/-+$/, '')
            if (!query || query.startsWith('-')) {
                setShowSuggestions(false)
            } else {
                setSuggestionType('mention')
                void searchUsers(query, protocol).then(results => {
                    setSuggestions(results)
                    setHashtagSuggestions([])
                    setShowSuggestions(results.length > 0)
                    setSelectedIndex(0)
                    if (results.length > 0) updateDropdownPos()
                })
            }
        } else {
            setShowSuggestions(false)
        }
    }

    // Handle mention selection
    const selectMention = (suggestion: SearchResult) => {
        if (!inputRef.current) return

        const cursorPos = inputRef.current.selectionStart
        const textBeforeCursor = value.slice(0, cursorPos)
        const textAfterCursor = value.slice(cursorPos)
        const match = textBeforeCursor.match(/@([\w-]*)$/)

        if (match) {
            const beforeMention = textBeforeCursor.slice(0, match.index)
            const handle = suggestion.handle
            const newText = `${beforeMention}@${handle} ${textAfterCursor}`
            onChange(newText)
            setShowSuggestions(false)

            // Set cursor position after mention
            setTimeout(() => {
                if (inputRef.current) {
                    const newPos = beforeMention.length + handle.length + 2
                    inputRef.current.setSelectionRange(newPos, newPos)
                    inputRef.current.focus()
                }
            }, 0)
        }
    }

    // Handle hashtag selection
    const selectHashtag = (hashtag: HashtagResult) => {
        if (!inputRef.current) return

        const cursorPos = inputRef.current.selectionStart
        const textBeforeCursor = value.slice(0, cursorPos)
        const textAfterCursor = value.slice(cursorPos)
        const match = textBeforeCursor.match(/#(\w*)$/)

        if (match) {
            const beforeHashtag = textBeforeCursor.slice(0, match.index)
            const newText = `${beforeHashtag}#${hashtag.name} ${textAfterCursor}`
            onChange(newText)
            setShowSuggestions(false)

            // Set cursor position after hashtag
            setTimeout(() => {
                if (inputRef.current) {
                    const newPos = beforeHashtag.length + hashtag.name.length + 2
                    inputRef.current.setSelectionRange(newPos, newPos)
                    inputRef.current.focus()
                }
            }, 0)
        }
    }

    // Handle keyboard navigation in suggestions
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showSuggestions) return

        const totalSuggestions =
            suggestionType === 'mention' ? suggestions.length : hashtagSuggestions.length

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSelectedIndex(prev => (prev + 1) % totalSuggestions)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSelectedIndex(prev => (prev - 1 + totalSuggestions) % totalSuggestions)
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            if (suggestionType === 'mention' && suggestions[selectedIndex]) {
                selectMention(suggestions[selectedIndex])
            } else if (suggestionType === 'hashtag' && hashtagSuggestions[selectedIndex]) {
                selectHashtag(hashtagSuggestions[selectedIndex])
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false)
        }
    }

    // Generate highlighted HTML with proper escaping to prevent XSS
    const getHighlightedText = (text: string) => {
        if (!text) return ''

        // Clean up old react-mentions format first
        const cleanedText = cleanOldMentionFormat(text)

        // Then escape ALL user input to prevent XSS
        let result = escapeHtml(cleanedText)

        // Now apply highlighting by wrapping matches in spans
        // Uses rgb() color format to avoid the hashtag regex matching hex colors
        // (e.g. #3b82f6 in inline styles would be caught by the # hashtag pattern)
        const highlightColor = 'rgb(59, 130, 246)'

        // Highlight @mentions (@ symbol plus handle)
        result = result.replace(
            /(@[a-zA-Z0-9._-]+)/g,
            `<span style="color: ${highlightColor};">$1</span>`
        )

        // Highlight #hashtags (# symbol plus tag name)
        result = result.replace(
            /(#[a-zA-Z0-9_]+)/g,
            `<span style="color: ${highlightColor};">$1</span>`
        )

        // Highlight URLs with scheme (https://example.com)
        result = result.replace(
            SCHEME_URL_REGEX,
            `<span style="color: ${highlightColor};">$&</span>`
        )

        // Highlight schemeless URLs (example.com, whiteside.io)
        // Must have at least one dot and valid TLD (2+ chars)
        // Skip if already in a span (from mentions or hashtags)
        result = result.replace(
            SCHEMELESS_URL_REGEX,
            `<span style="color: ${highlightColor};">$&</span>`
        )

        return result
    }

    return (
        <div
            className={`rich-composer ${className} relative ${fillHeight ? 'flex-1 flex flex-col min-h-0' : ''}`}
        >
            <div
                ref={containerRef}
                className={`relative w-full transition-colors ${fillHeight ? 'flex-1' : ''} ${borderless ? 'min-h-[40px]' : 'min-h-[100px] bg-white/5 border border-border rounded-xl focus-within:border-primary focus-within:bg-white/10'}`}
            >
                {/* Highlighter layer - shows colored text on top */}
                <div
                    ref={highlighterRef}
                    data-testid="rich-composer-highlighter"
                    className={`absolute top-0 left-0 w-full h-full pointer-events-none ${autoGrow ? 'overflow-hidden' : 'overflow-auto'}`}
                    style={{
                        padding: '12px',
                        fontFamily:
                            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        color: '#f3f4f6', // Regular text color
                        zIndex: 1, // On top
                    }}
                    dangerouslySetInnerHTML={{ __html: getHighlightedText(value) }}
                />

                {/* Input layer - transparent text underneath */}
                <textarea
                    ref={inputRef}
                    value={value}
                    onChange={handleChange}
                    onScroll={handleScroll}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className={`relative w-full ${autoGrow ? '' : 'h-full'} bg-transparent border-none outline-none resize-none placeholder:text-text-secondary`}
                    style={{
                        padding: '12px',
                        fontFamily:
                            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        minHeight: fillHeight ? '100%' : borderless ? '40px' : '100px',
                        overflowY: autoGrow ? 'hidden' : undefined,
                        color: 'transparent', // Hide text, show highlighter instead
                        caretColor: '#f3f4f6', // But keep cursor visible
                        zIndex: 0, // Underneath
                    }}
                />
            </div>

            {/* Suggestions dropdown - rendered via portal for correct positioning */}
            {showSuggestions &&
                createPortal(
                    <div
                        className="fixed z-[9999] bg-[#1e1e1e] border border-[#333] rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto"
                        style={{
                            top: dropdownPos.top + 4,
                            left: dropdownPos.left,
                            width: dropdownPos.width,
                        }}
                    >
                        {suggestionType === 'mention' &&
                            suggestions.map((suggestion, index) => {
                                const handle = suggestion.displayName ?? suggestion.handle
                                return (
                                    <div
                                        key={suggestion.handle || index}
                                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-[#333] last:border-b-0 ${
                                            index === selectedIndex
                                                ? 'bg-primary text-white'
                                                : 'text-text-secondary hover:bg-primary/20'
                                        }`}
                                        onMouseDown={e => {
                                            e.preventDefault()
                                            selectMention(suggestion)
                                        }}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        {suggestion.avatarUrl && (
                                            <img
                                                src={suggestion.avatarUrl}
                                                alt=""
                                                className="w-6 h-6 rounded-full bg-gray-700"
                                            />
                                        )}
                                        <span className="truncate">@{handle}</span>
                                    </div>
                                )
                            })}
                        {suggestionType === 'hashtag' &&
                            hashtagSuggestions.map((hashtag, index) => {
                                return (
                                    <div
                                        key={hashtag.name}
                                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-[#333] last:border-b-0 ${
                                            index === selectedIndex
                                                ? 'bg-primary text-white'
                                                : 'text-text-secondary hover:bg-primary/20'
                                        }`}
                                        onMouseDown={e => {
                                            e.preventDefault()
                                            selectHashtag(hashtag)
                                        }}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        <span className="truncate">#{hashtag.name}</span>
                                        {hashtag.history && hashtag.history.length > 0 && (
                                            <span className="ml-auto text-xs opacity-60">
                                                {hashtag.history[0].uses} uses
                                            </span>
                                        )}
                                    </div>
                                )
                            })}
                    </div>,
                    document.body
                )}
        </div>
    )
}
